// Jobs view — list of recent jobs as glass-panel rows. Click to expand
// inline with result/error payload. Polls every 2s so running jobs animate
// their progress and freshly-completed ones surface without a manual reload.

import { useInfiniteQuery } from "@tanstack/react-query";
import {
	AlertCircle,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	CircleDashed,
	Loader2,
	XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { GlassPanel, Pill } from "@/components/glass";

type JobStatus = "pending" | "running" | "completed" | "failed" | "cancelled";

interface JobRow {
	id: string;
	projectId: string;
	type: string;
	status: JobStatus;
	input: unknown;
	result: unknown;
	progress: { processed?: number; total?: number; phase?: string };
	error: string | null;
	createdAt: string;
	startedAt: string | null;
	completedAt: string | null;
	cancelledAt: string | null;
}

// Per-page cap — matches the API's hard cap (server clamps to 200).
// Smaller batches keep "load more" feeling responsive on slow networks.
const PAGE_SIZE = 50;

interface JobsPage {
	jobs: JobRow[];
	next_cursor: string | null;
}

export default function JobsView() {
	const { id: projectId } = useParams<{ id: string }>();
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const jobsQ = useInfiniteQuery({
		queryKey: ["jobs", projectId],
		// Polls every 2s so running jobs animate progress and freshly-
		// completed ones surface without a manual reload. Note that
		// react-query refetches ALL loaded pages on the interval — fine
		// at PAGE_SIZE=50 with a handful of pages; revisit if that ever
		// becomes a perf issue (e.g. cap refetch to the first page).
		refetchInterval: 2000,
		initialPageParam: null as string | null,
		queryFn: async ({ pageParam }) => {
			const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
			if (pageParam) params.set("cursor", pageParam);
			const r = await fetch(
				`/api/projects/${projectId}/jobs?${params.toString()}`,
				{ credentials: "include" },
			);
			if (!r.ok) throw new Error(`jobs: ${r.status}`);
			return (await r.json()) as JobsPage;
		},
		getNextPageParam: (last) => last.next_cursor,
		enabled: !!projectId,
	});

	const toggle = (id: string) => {
		setExpanded((s) => {
			const next = new Set(s);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const jobs: JobRow[] =
		jobsQ.data?.pages.flatMap((p) => p.jobs) ?? [];

	return (
		<div className="h-full overflow-y-auto pt-1 pb-4">
			<div className="max-w-[1100px] mx-auto space-y-2">
				{jobs.length === 0 && !jobsQ.isLoading && (
					<div className="flex items-center justify-center h-[200px] text-[13px] text-[var(--color-text-muted)]">
						<div className="text-center space-y-1">
							<div>No jobs yet.</div>
							<div className="text-[var(--color-text-dim)] mono">
								v3 ingest-file 'README.md' --source notes --wait
							</div>
						</div>
					</div>
				)}
				{jobs.map((j) => (
					<GlassPanel
						key={j.id}
						className="overflow-hidden transition-colors hover:bg-[var(--color-surface-hi)]"
					>
						<button
							type="button"
							onClick={() => toggle(j.id)}
							className="w-full flex items-center gap-3 px-4 py-3 text-left"
						>
							<StatusIcon status={j.status} />
							<div className="flex-1 min-w-0">
								<div className="flex items-baseline gap-2">
									<span className="text-[13px] truncate">{labelFor(j.type)}</span>
									<span className="text-[11px] text-[var(--color-text-dim)] mono truncate">
										{j.id}
									</span>
								</div>
								<div className="text-[11px] text-[var(--color-text-muted)] flex items-center gap-3">
									<span>{relativeTime(j.createdAt)}</span>
									<span className="text-[var(--color-text-dim)]">·</span>
									<span>{durationFor(j)}</span>
									{j.status === "running" && j.progress.total ? (
										<>
											<span className="text-[var(--color-text-dim)]">·</span>
											<span className="mono">
												{j.progress.processed ?? 0}/{j.progress.total}
												{j.progress.phase ? ` ${j.progress.phase}` : ""}
											</span>
										</>
									) : null}
								</div>
							</div>
							<StatusPill status={j.status} />
							{j.status === "failed" && parseQuotaError(j.error) ? (
								<QuotaChip parsed={parseQuotaError(j.error)!} />
							) : null}
							{expanded.has(j.id) ? (
								<ChevronDown className="w-4 h-4 text-[var(--color-text-dim)]" />
							) : (
								<ChevronRight className="w-4 h-4 text-[var(--color-text-dim)]" />
							)}
						</button>
						{expanded.has(j.id) && (
							<div className="border-t border-[var(--color-border)] px-4 py-3 space-y-3 bg-black/20">
								{j.error && parseQuotaError(j.error) ? (
									<QuotaErrorBlock parsed={parseQuotaError(j.error)!} />
								) : j.error ? (
									<div>
										<Label>error</Label>
										<pre className="mono text-[11px] text-[var(--color-danger)] whitespace-pre-wrap break-words">
											{j.error}
										</pre>
									</div>
								) : null}
								<IngestStatSummary result={j.result} />
								<div>
									<Label>result</Label>
									<pre className="mono text-[11px] text-[var(--color-text-muted)] whitespace-pre-wrap break-words bg-black/30 rounded-md p-2 border border-[var(--color-border)] max-h-[280px] overflow-y-auto">
										{prettyJson(j.result)}
									</pre>
								</div>
								<div>
									<Label>input</Label>
									<pre className="mono text-[11px] text-[var(--color-text-dim)] whitespace-pre-wrap break-words bg-black/30 rounded-md p-2 border border-[var(--color-border)] max-h-[120px] overflow-y-auto">
										{prettyJson(j.input)}
									</pre>
								</div>
							</div>
						)}
					</GlassPanel>
				))}

				{jobsQ.hasNextPage && (
					<div className="flex justify-center pt-2">
						<Pill
							onClick={() => jobsQ.fetchNextPage()}
							disabled={jobsQ.isFetchingNextPage}
						>
							{jobsQ.isFetchingNextPage ? "Loading…" : "Load more"}
						</Pill>
					</div>
				)}
				{!jobsQ.hasNextPage && jobs.length >= PAGE_SIZE && (
					<div className="text-center pt-2 text-[11px] text-[var(--color-text-dim)]">
						End of jobs.
					</div>
				)}
			</div>
		</div>
	);
}

/**
 * Stat-strip summary for ingest-shaped results. Reads
 *   { nodes_inserted, nodes_updated, edges_inserted, edges_dangling,
 *     edges_resolved_cross_batch, items }
 * (plus skipped/communities/members for community jobs) and renders
 * a compact row that's easier to skim than the raw JSON. Shows nothing
 * when the result doesn't look like an ingest payload.
 *
 * `edges_dangling > 0` gets a warning hint — these are edges that
 * referenced an external_id no node in the corpus uses yet (typical
 * cause: agent ingested entities in a batch that referenced something
 * not yet ingested elsewhere).
 */
function IngestStatSummary({ result }: { result: unknown }) {
	if (!result || typeof result !== "object") return null;
	const r = result as Record<string, unknown>;

	// Ingest-job result shape detection: at least one of the ingest keys.
	const isIngest =
		typeof r.nodes_inserted === "number" ||
		typeof r.edges_inserted === "number" ||
		typeof r.items === "number";
	const isCommunity =
		typeof r.communities === "number" || typeof r.members === "number";

	if (!isIngest && !isCommunity) return null;

	const cells: { label: string; value: string }[] = [];
	const num = (k: string) => (typeof r[k] === "number" ? (r[k] as number) : 0);

	if (isIngest) {
		const ins = num("nodes_inserted");
		const upd = num("nodes_updated");
		const eIns = num("edges_inserted");
		const eDang = num("edges_dangling");
		const eXBatch = num("edges_resolved_cross_batch");
		const items = num("items");
		if (items) cells.push({ label: "items", value: String(items) });
		cells.push({ label: "nodes +", value: String(ins) });
		if (upd > 0) cells.push({ label: "nodes ~", value: String(upd) });
		cells.push({ label: "edges +", value: String(eIns) });
		if (eXBatch > 0)
			cells.push({ label: "cross-batch", value: String(eXBatch) });
		if (eDang > 0) cells.push({ label: "dangling", value: String(eDang) });
	}
	if (isCommunity) {
		if (r.skipped) {
			cells.push({ label: "status", value: String(r.skipped) });
		} else {
			cells.push({ label: "communities", value: String(num("communities")) });
			cells.push({ label: "members", value: String(num("members")) });
		}
	}

	const eDang = num("edges_dangling");

	return (
		<div className="space-y-2">
			<div>
				<Label>summary</Label>
				<div className="flex flex-wrap gap-3 text-[11px] text-[var(--color-text-muted)] mono">
					{cells.map((c) => (
						<span key={c.label}>
							<span className="text-[var(--color-text-dim)]">{c.label}</span>{" "}
							<span className="text-[var(--color-text)]">{c.value}</span>
						</span>
					))}
				</div>
			</div>
			{eDang > 0 && (
				<div className="text-[11px] text-amber-300/80 bg-amber-300/5 border border-amber-300/10 rounded-md px-3 py-2">
					{eDang} edge{eDang === 1 ? "" : "s"} couldn't link to existing
					entities — they reference an external_id no node in this project
					uses yet. Re-ingest the upstream batch (or extract from source again)
					to resolve.
				</div>
			)}
		</div>
	);
}

function Label({ children }: { children: React.ReactNode }) {
	return (
		<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-1">
			{children}
		</div>
	);
}

function StatusIcon({ status }: { status: JobStatus }) {
	switch (status) {
		case "running":
			return <Loader2 className="w-4 h-4 animate-spin text-[var(--color-running)]" />;
		case "completed":
			return <CheckCircle2 className="w-4 h-4 text-[var(--color-success)]" />;
		case "failed":
			return <XCircle className="w-4 h-4 text-[var(--color-danger)]" />;
		case "cancelled":
			return <AlertCircle className="w-4 h-4 text-[var(--color-text-dim)]" />;
		case "pending":
		default:
			return <CircleDashed className="w-4 h-4 text-[var(--color-text-dim)]" />;
	}
}

function StatusPill({ status }: { status: JobStatus }) {
	const cls =
		status === "completed"
			? "text-[var(--color-success)] border-[rgba(34,197,94,0.2)]"
			: status === "failed"
				? "text-[var(--color-danger)] border-[rgba(239,68,68,0.2)]"
				: status === "running"
					? "text-[var(--color-running)] border-[rgba(56,189,248,0.25)]"
					: "text-[var(--color-text-dim)] border-[var(--color-border)]";
	return (
		<span
			className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls}`}
		>
			{status}
		</span>
	);
}

interface QuotaError {
	reason: string;
	quota: string | null;
	resetAt: Date | null;
}

/**
 * Spec 20 PR 3 #4. Detect the `blocked_by_quota:` prefix the ingest
 * workers throw when checkEntitlement refuses an operation, and parse
 * the suffix fields they append. Returns null for any other failure.
 */
function parseQuotaError(error: string | null): QuotaError | null {
	if (!error) return null;
	if (!error.startsWith("blocked_by_quota:")) return null;
	let body = error.slice("blocked_by_quota:".length).trim();

	let quota: string | null = null;
	const quotaMatch = body.match(/\(quota=([^)]+)\)/);
	if (quotaMatch) {
		quota = quotaMatch[1];
		body = body.replace(quotaMatch[0], "").trim();
	}

	let resetAt: Date | null = null;
	const resetMatch = body.match(/resets_at=(\S+)/);
	if (resetMatch) {
		const t = new Date(resetMatch[1]);
		if (!Number.isNaN(t.getTime())) resetAt = t;
		body = body.replace(resetMatch[0], "").trim();
	}

	// Strip any remaining worker-appended diagnostic suffixes like
	// `preflight_bytes=12345`.
	body = body.replace(/\b[a-z_]+=\S+/g, "").trim();

	return {
		reason: body || "quota exceeded",
		quota,
		resetAt,
	};
}

function QuotaChip({ parsed }: { parsed: QuotaError }) {
	const suffix = parsed.resetAt
		? ` · resets ${formatResetDate(parsed.resetAt)}`
		: "";
	return (
		<span
			className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border text-[var(--color-warning,#f59e0b)] border-[rgba(245,158,11,0.3)]"
			title={parsed.reason}
		>
			quota exceeded{suffix}
		</span>
	);
}

function QuotaErrorBlock({ parsed }: { parsed: QuotaError }) {
	return (
		<div className="rounded-md border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.05)] p-3 text-[12px] space-y-1">
			<div className="font-medium text-[var(--color-warning,#f59e0b)]">
				Quota exceeded
			</div>
			<div className="text-[var(--color-text-muted)]">{parsed.reason}</div>
			{parsed.quota && (
				<div className="text-[11px] text-[var(--color-text-dim)] mono">
					limit: {parsed.quota}
				</div>
			)}
			{parsed.resetAt && (
				<div className="text-[11px] text-[var(--color-text-dim)]">
					Resets {formatResetDate(parsed.resetAt)}
				</div>
			)}
			<div className="pt-1 text-[11px]">
				<Link
					to="/account/usage"
					className="text-[var(--color-text)] underline hover:no-underline"
				>
					View usage →
				</Link>
			</div>
		</div>
	);
}

function formatResetDate(d: Date): string {
	const diffMs = d.getTime() - Date.now();
	if (diffMs <= 0) return "now";
	const hours = Math.floor(diffMs / (60 * 60 * 1000));
	if (hours < 24) return `in ${hours}h`;
	const days = Math.floor(hours / 24);
	if (days < 14) return `in ${days}d`;
	return d.toLocaleDateString();
}

function labelFor(type: string): string {
	switch (type) {
		case "ingest":
			return "Ingest";
		case "extract":
			return "Extract";
		case "structured":
			return "Ingest (structured)";
		case "ask":
			return "Ask";
		case "community":
			return "Community";
		default:
			return type;
	}
}

function relativeTime(iso: string): string {
	const d = new Date(iso);
	const diff = Date.now() - d.getTime();
	if (diff < 0) return "just now";
	const s = Math.round(diff / 1000);
	if (s < 60) return `${s}s ago`;
	const m = Math.round(s / 60);
	if (m < 60) return `${m}m ago`;
	const h = Math.round(m / 60);
	if (h < 24) return `${h}h ago`;
	const days = Math.round(h / 24);
	return `${days}d ago`;
}

function durationFor(j: JobRow): string {
	const start = j.startedAt ? new Date(j.startedAt).getTime() : null;
	const end = j.completedAt
		? new Date(j.completedAt).getTime()
		: j.cancelledAt
			? new Date(j.cancelledAt).getTime()
			: null;
	if (!start) return "—";
	const ms = (end ?? Date.now()) - start;
	if (ms < 1000) return `${ms}ms`;
	if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
	return `${Math.round(ms / 1000)}s`;
}

function prettyJson(v: unknown): string {
	if (v === null || v === undefined) return "—";
	try {
		return JSON.stringify(v, null, 2);
	} catch {
		return String(v);
	}
}
